/**
 * Componente para preview do destino do arquivo
 */

import React, { useMemo } from 'react';
import { FolderOpen } from 'lucide-react';
import { Cliente, ProdutoCategoriaItem } from '../../../../types/gerArquivos';
import { slugifyName } from '../../../../utils/pathTemplates';
import { getCategoriaPastaMap } from '../../../../utils/fileNaming';

interface PreviewDestinoProps {
  cliente: Cliente | null;
  isConfirmed: boolean;
  digits: string; // Mantido para compatibilidade, mas não usado no preview
  prodCatItem: 1 | 2 | 3 | 4;
  categorias: ProdutoCategoriaItem[];
  userId: number;
  userName: string;
}

export const PreviewDestino: React.FC<PreviewDestinoProps> = ({
  cliente,
  isConfirmed,
  // digits, // Não usado no preview atual
  prodCatItem,
  categorias,
  userId,
  userName
}) => {
  const dirPreview = useMemo(() => {
    const now = new Date();
    const yyyy = String(now.getFullYear());
    const pad2 = (n: number) => String(n).padStart(2, '0');
    const mmNum = now.getMonth() + 1;
    const MONTHS = [
      'JANEIRO', 'FEVEREIRO', 'MARÇO', 'ABRIL', 'MAIO', 'JUNHO',
      'JULHO', 'AGOSTO', 'SETEMBRO', 'OUTUBRO', 'NOVEMBRO', 'DEZEMBRO'
    ];
    const monthLabel = `${pad2(mmNum)}-${MONTHS[mmNum - 1]}`;
    const dd = pad2(now.getDate());
    const dayLabel = `${dd}-${pad2(mmNum)}-${yyyy}`;

    // Usar mapeamento dinâmico de fileNaming.ts (carrega da API automaticamente)
    const categoriaMap = getCategoriaPastaMap();
    const cat = categoriaMap[prodCatItem] || 
                categorias.find(c => (c.id as 1 | 2 | 3 | 4) === prodCatItem)?.prod_tipo_descricao || 
                'INDEFINIDO';

    const hasCadastro = !!cliente && isConfirmed;

    if (hasCadastro && cliente) {
      // Cliente cadastrado
      const fantasia = slugifyName(cliente.empresa || cliente.nome).toUpperCase();
      const raiz = `${cliente.id}_${fantasia}`;
      return `${raiz}\\${yyyy}\\${monthLabel}\\${dayLabel}\\${cat}`;
    }

    // Sem cadastro
    const uid = String(userId || 0);
    const uname = slugifyName(userName || 'usuario').toUpperCase();
    const raizSemCadastro = `SEM-CADASTRO\\${uid}_${uname}`;
    return `${raizSemCadastro}\\${yyyy}\\${monthLabel}\\${dayLabel}\\${cat}`;
  }, [cliente, isConfirmed, prodCatItem, categorias, userId, userName]);

  return (
    <div className="space-y-2">
      <label className="block text-xs text-gray-300 font-medium flex items-center gap-1.5">
        <FolderOpen className="w-3.5 h-3.5" />
        Pré-visualização do destino
      </label>
      
      <div className="p-2 bg-navy-800 border border-navy-600 rounded-lg">
        <p className="text-[10px] text-gray-400 mb-1.5 font-medium">Caminho relativo:</p>
        <code className="text-[10px] text-blue-300 break-all font-mono leading-relaxed block">
          {dirPreview}
        </code>
        
        <div className="mt-2 pt-2 border-t border-navy-700">
          <p className="text-[10px] text-gray-500">
            {cliente && isConfirmed ? (
              <>
                <span className="text-green-400 font-medium">✓ Cliente cadastrado</span>
                {' · '}
                Arquivo será salvo na pasta do cliente
              </>
            ) : (
              <>
                <span className="text-yellow-400 font-medium">⚠ Sem cadastro</span>
                {' · '}
                Arquivo será salvo na pasta SEM-CADASTRO
              </>
            )}
          </p>
        </div>
      </div>
    </div>
  );
};

